import type {
  AnyPurchaseAutocompleteItem,
  CaiAutocompleteItem,
  DimensionAutocompleteItem,
  EanAutocompleteItem,
  IOptionItem,
  ISuccessApiResponse,
  RecordValues,
  ReferenceAutocompleteItem,
} from '@wyz/types';
import { PublicTypeAheadFieldProps, TypeAheadFetcher } from '@wyz/ui';
import { SEARCH_FIELDS, invariant, isEmptyValue, serialize } from '@wyz/utils';
import { isNil, omitBy, orderBy, uniqBy } from 'lodash-es';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { TypeaheadField } from '~/common/components';
import { Trans, useApi, useConfig, useI18n } from '~/common/context';
import { usePurchaseContext } from '~/purchase/context';
import {
  DEFAULT_VEHICLE_TYPE,
  TYRE_AUTOCOMPLETE_SEARCH_FIELD_NAME_LOOKUP,
} from '~/purchase/helpers';
import {
  IAutocompleteParameterQuery,
  IBaseSearchFormSchema,
  ISeason,
} from '~/purchase/types';
type Props = Pick<
  PublicTypeAheadFieldProps<false, IOptionItem & AnyPurchaseAutocompleteItem>,
  'size'
> & {
  dimensionQuery: IAutocompleteParameterQuery;
  availableSeasons: Array<ISeason>;
  vehicleTypeGroup: IBaseSearchFormSchema['vehicleTypeGroup'];
  label?: React.ReactNode;
  placeholder?: React.ReactNode;
  disabled?: boolean;
  requestedFields?: Array<RecordValues<typeof SEARCH_FIELDS>>;
  partialDimension?: boolean;
};
function TyreAutocomplete({
  dimensionQuery,
  vehicleTypeGroup,
  label = <Trans i18nKey='app.purchase.search_page.quick_search' />,
  placeholder = (
    <Trans i18nKey='app.purchase.search_page.quick_search_placeholder' />
  ),
  disabled,
  requestedFields = [
    SEARCH_FIELDS.DIMENSION,
    SEARCH_FIELDS.EAN,
    SEARCH_FIELDS.CAI,
    SEARCH_FIELDS.REFERENCE,
  ],
  availableSeasons,
  partialDimension = true,
  ...props
}: Props) {
  const methods = useFormContext<IBaseSearchFormSchema>();

  const { t } = useI18n();
  const { callCoreApi } = useApi();
  const { state } = usePurchaseContext();
  const { tenantCode } = useConfig();
  const idCustomer = state.targetCustomer?.id;
  invariant(idCustomer);

  const fetchByDimension: TypeAheadFetcher<
    IOptionItem & AnyPurchaseAutocompleteItem
  > = async (keyword, { signal }) => {
    const data = await callCoreApi<
      ISuccessApiResponse<Array<DimensionAutocompleteItem>>
    >(
      `/tenants/api/wtb/tenants/${tenantCode}/customers/${idCustomer}/tyres/autocomplete/dimension/${encodeURIComponent(
        keyword,
      )}?${serialize(
        omitBy(
          {
            sortOrder: 1,
            partialDimension: partialDimension ? 1 : 0,
            vehicleType: dimensionQuery.vehicleTypes,
            searchType: dimensionQuery.searchType,
          },
          isEmptyValue,
        ),
      )}`,
      { signal },
    );

    const sorted = orderBy(
      uniqBy(data.data, 'dimension'),
      [
        'width',
        'height',
        'diameter',
        'item.loadIndexes.length',
        'item.speedIndexes.length',
        'dimension',
      ],
      'asc',
    );

    return sorted.map((item) => ({
      ...item,
      label: item.dimension,
      value: item.dimension,
    }));
  };
  const fetchByEan: TypeAheadFetcher<
    IOptionItem & AnyPurchaseAutocompleteItem
  > = async (keyword, { signal }) => {
    const data = await callCoreApi<
      ISuccessApiResponse<Array<EanAutocompleteItem>>
    >(
      `/tenants/api/wtb/tenants/${tenantCode}/customers/${idCustomer}/tyres/autocomplete/ean/${encodeURIComponent(
        keyword,
      )}?${serialize(
        omitBy(
          {
            sortOrder: 1,
            vehicleType: dimensionQuery.vehicleTypes,
            searchType: dimensionQuery.searchType,
          },
          isEmptyValue,
        ),
      )}`,
      { signal },
    );

    return uniqBy(data.data, 'ean').map((item) => ({
      ...item,
      label: item.ean,
      value: item.ean,
    }));
  };
  const fetchByCai: TypeAheadFetcher<
    IOptionItem & AnyPurchaseAutocompleteItem
  > = async (keyword, { signal }) => {
    const data = await callCoreApi<
      ISuccessApiResponse<Array<CaiAutocompleteItem>>
    >(
      `/tenants/api/wtb/tenants/${tenantCode}/customers/${idCustomer}/tyres/autocomplete/cai/${encodeURIComponent(
        keyword,
      )}?${serialize(
        omitBy(
          {
            sortOrder: 1,
            vehicleType: dimensionQuery.vehicleTypes,
            searchType: dimensionQuery.searchType,
          },
          isEmptyValue,
        ),
      )}`,
      { signal },
    );

    return uniqBy(data.data, 'supplierCode').map((item) => ({
      ...item,
      label: item.supplierCode,
      value: item.supplierCode,
    }));
  };
  const fetchByReference: TypeAheadFetcher<
    IOptionItem & AnyPurchaseAutocompleteItem
  > = async (keyword, { signal }) => {
    const data = await callCoreApi<
      ISuccessApiResponse<Array<ReferenceAutocompleteItem>>
    >(
      `/tenants/api/wtb/tenants/${tenantCode}/customers/${idCustomer}/tyres/autocomplete/reference/${encodeURIComponent(keyword)}?${serialize(
        omitBy(
          {
            vehicleType: dimensionQuery.vehicleTypes,
            searchType: dimensionQuery.searchType,
          },
          isEmptyValue,
        ),
      )}`,
      { signal },
    );

    return uniqBy(data.data, 'reference').map((item) => ({
      ...item,
      label: item.reference,
      value: item.reference,
    }));
  };

  const resetFields = () => {
    methods.setValue(
      'seasons',
      availableSeasons.map((season) => season.publicKey),
    );
    methods.setValue('brands', null);
    methods.setValue('width', null);
    methods.setValue('height', null);
    methods.setValue('diameter', null);
    methods.setValue('loadIndexes', null);
    methods.setValue('speedIndexes', null);
  };

  const handleInputChange = (inputValue: string) => {
    methods.setValue('rawIdentifier', inputValue);
  };

  const setDimensionFields = (item: AnyPurchaseAutocompleteItem) => {
    methods.setValue('width', item.meta.width);
    methods.setValue('height', item.meta.height);
    methods.setValue('diameter', item.meta.diameter);
    methods.setValue(
      'loadIndexes',
      item.meta.loadIndexes.filter((loadIndex) => loadIndex > 0),
    );

    methods.setValue(
      'speedIndexes',
      item.meta.speedIndexes.filter((speedIndex) => !isEmptyValue(speedIndex)),
    );
  };
  return (
    <TypeaheadField<false, IOptionItem & AnyPurchaseAutocompleteItem>
      name='identifier'
      label={label}
      placeholder={placeholder}
      fetcher={
        [
          requestedFields.includes(SEARCH_FIELDS.DIMENSION) && fetchByDimension,
          requestedFields.includes(SEARCH_FIELDS.EAN) && fetchByEan,
          requestedFields.includes(SEARCH_FIELDS.CAI) && fetchByCai,
          requestedFields.includes(SEARCH_FIELDS.REFERENCE) && fetchByReference,
        ].filter(Boolean) as Array<
          TypeAheadFetcher<IOptionItem & AnyPurchaseAutocompleteItem>
        >
      }
      isDisabled={disabled || !vehicleTypeGroup}
      additionalQueryKeys={[
        ...dimensionQuery.vehicleTypes,
        dimensionQuery.searchType,
      ]}
      minCharacter={vehicleTypeGroup === DEFAULT_VEHICLE_TYPE ? 5 : 3}
      isClearable
      size='sm'
      onSelect={(item) => {
        let field: IBaseSearchFormSchema['fieldNameSearch'] = undefined;
        if (item && 'dimension' in item) {
          setDimensionFields(item);
          methods.resetField('seasons', {
            defaultValue: availableSeasons.map((season) => season.publicKey),
          });
          field = SEARCH_FIELDS.DIMENSION;
        } else if (item) {
          resetFields();
          if ('ean' in item) {
            const frameZoneNotFound = isNil(item.meta.priceFrame);
            if (item.meta.referenceFrameGroupMismatch || frameZoneNotFound) {
              methods.setError('identifier', {
                message: t('app.purchase.search_page.errors.unavailable_ean'),
              });
              return;
            }
          }

          setDimensionFields(item);
          if (item.meta.season) {
            methods.setValue('seasons', [item.meta.season.publicKey]);
          }
          if (item.meta.brand) {
            methods.setValue('brands', [item.meta.brand.publicKey]);
          }

          if ('supplierCode' in item) {
            field = SEARCH_FIELDS.CAI;
          } else if ('ean' in item) {
            field = SEARCH_FIELDS.EAN;
          } else if ('reference' in item) {
            field = SEARCH_FIELDS.REFERENCE;
          }
        } else {
          // unselect case => clear everything
          methods.setValue('rawIdentifier', null);
          methods.setValue('identifier', null);
          resetFields();
        }

        methods.clearErrors('identifier');
        methods.setValue('fieldNameSearch', field);
      }}
      mapper={(groups) => {
        const mappers = [];
        let groupIndex = 0;

        if (requestedFields.includes(SEARCH_FIELDS.DIMENSION)) {
          mappers.push({
            label: t(
              TYRE_AUTOCOMPLETE_SEARCH_FIELD_NAME_LOOKUP[
                SEARCH_FIELDS.DIMENSION
              ],
            ),
            options: groups[groupIndex] || [],
          });
          groupIndex++;
        }
        if (requestedFields.includes(SEARCH_FIELDS.EAN)) {
          mappers.push({
            label: t(
              TYRE_AUTOCOMPLETE_SEARCH_FIELD_NAME_LOOKUP[SEARCH_FIELDS.EAN],
            ),
            options: groups[groupIndex] || [],
          });
          groupIndex++;
        }
        if (requestedFields.includes(SEARCH_FIELDS.CAI)) {
          mappers.push({
            label: t(
              TYRE_AUTOCOMPLETE_SEARCH_FIELD_NAME_LOOKUP[SEARCH_FIELDS.CAI],
            ),
            options: groups[groupIndex] || [],
          });
          groupIndex++;
        }
        if (requestedFields.includes(SEARCH_FIELDS.REFERENCE)) {
          mappers.push({
            label: t(
              TYRE_AUTOCOMPLETE_SEARCH_FIELD_NAME_LOOKUP[
                SEARCH_FIELDS.REFERENCE
              ],
            ),
            options: groups[groupIndex] || [],
          });
        }
        return mappers;
      }}
      showCount={false}
      onInputChange={handleInputChange}
      {...props}
    />
  );
}

export default TyreAutocomplete;
